/**
 * THE APPOINTMENT STATUSES, IN ONE PLACE (#254).
 *
 * ⛔ EXTRACTED FROM Calendar.tsx ON 2026-08-26, WHEN THE SEARCH PANEL NEEDED THEM
 * TOO. The list was an inline array inside the status-key strip; copying it into
 * the search filter would have been two lists that must agree forever — the exact
 * fault `matchedOn` is computed server-side to avoid, and the one that already bit
 * this codebase once when a label→field ternary covered seven of thirteen cases.
 *
 * ⚠️ THE CODES ARE dock.AppointmentStatus.Code — the readable key, not StatusId.
 * The ids are a database detail that a screen must never learn: a query written
 * against numbers is silently wrong the day they are reseeded.
 *
 * ⚠️ AND THIS IS STILL A CLIENT-SIDE COPY OF A DATABASE TABLE. It is accepted
 * because these seven are structural — every one of them has code branching on it
 * somewhere — but a status added in SQL and not here simply will not be offered as
 * a filter. #259 tracks serving them from `lookups` instead.
 */

export interface AppointmentStatusOption {
    /** dock.AppointmentStatus.Code */
    code: string;
    label: string;
    /** The tailwind swatch class the calendar's status key uses. */
    swatch: string;
    /**
     * ⛔ BLOCK IS NOT AN APPOINTMENT. It is a reserved slot, so it can be FILTERED
     * on but must never be offered as a bulk target — "approve 4 blocks" is not a
     * sentence that means anything.
     */
    bulkTarget: boolean;
}

export const APPOINTMENT_STATUSES: readonly AppointmentStatusOption[] = [
    { code: 'REQUESTED', label: 'Requested', swatch: 'bg-status-requested', bulkTarget: true },
    { code: 'APPROVED',  label: 'Approved',  swatch: 'bg-status-approved',  bulkTarget: true },
    { code: 'ARRIVED',   label: 'Arrived',   swatch: 'bg-status-arrived',   bulkTarget: false },
    { code: 'DEPARTED',  label: 'Departed',  swatch: 'bg-status-departed',  bulkTarget: false },
    { code: 'NOSHOW',    label: 'No-show',   swatch: 'bg-status-noshow',    bulkTarget: true },
    { code: 'CANCELLED', label: 'Cancelled', swatch: 'bg-status-cancelled', bulkTarget: true },
    { code: 'BLOCK',     label: 'Blocked',   swatch: 'bg-slate-500',        bulkTarget: false },
];

export const statusLabel = (code: string | null | undefined): string =>
    APPOINTMENT_STATUSES.find(s => s.code === code)?.label ?? (code ?? '—');

/**
 * ⛔ THE BULK ACTIONS, AND WHY THERE ARE ONLY THREE.
 *
 * Pete, 2026-08-26: *"the ability to change all to a certain status, like approved
 * or cancelled or void/delete"*.
 *
 * ⚠️ "void/delete" AND "cancelled" ARE THE SAME OPERATION HERE. There is no
 * scheduler hard-delete anywhere in this application, deliberately: an appointment
 * carries audit history, documents and driver check-ins, and CANCELLED is the
 * terminal state that keeps them attached to it. Offering a "Delete" that quietly
 * cancelled would be a button whose label disagrees with what it does.
 *
 * ⛔ ARRIVED AND DEPARTED ARE ABSENT ON PURPOSE. They are observations of a
 * physical event at a specific time — `markArrived` records WHEN. Setting forty of
 * them at once would stamp forty trucks as having arrived at a moment none of them
 * did, and every dwell-time number downstream would be wrong and unrecoverable.
 */
export interface BulkActionOption {
    action: 'APPROVE' | 'CANCEL' | 'NOSHOW';
    label: string;
    /** ⚠️ Rendered in the confirm dialog. Says what will happen, not what it is called. */
    describe: (n: number) => string;
    destructive: boolean;
}

export const BULK_ACTIONS: readonly BulkActionOption[] = [
    {
        action: 'APPROVE',
        label: 'Approve',
        describe: n => `Approve ${n} appointment${n === 1 ? '' : 's'}. Only requested ones can be approved — anything else is left alone and reported.`,
        destructive: false,
    },
    {
        action: 'NOSHOW',
        label: 'Mark no-show',
        describe: n => `Mark ${n} appointment${n === 1 ? '' : 's'} as no-show. Only requested or approved ones can be marked — anything already arrived or departed is left alone.`,
        destructive: false,
    },
    {
        /**
         * ⛔ LAST IN THE LIST AND FLAGGED DESTRUCTIVE, so it is not adjacent to
         * Approve. A mis-click between two neighbouring items in a dropdown is the
         * difference between confirming a day's bookings and cancelling them.
         */
        action: 'CANCEL',
        label: 'Cancel / void',
        describe: n => `Cancel ${n} appointment${n === 1 ? '' : 's'}. This is what "void" means here — the booking ends but its history, paperwork and check-ins stay attached. It cannot be undone from this screen.`,
        destructive: true,
    },
];
